import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import * as subscriptionService from '../services/subscriptionService';
import PricingModal from './PricingModal';
import { generateReferralCode, getReferralStats } from '../services/api';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';

/** Stripe subscription secondary line aligned with mobile status messaging (#190) */
function getStripeSubscriptionSecondaryText(subscription, isTrialActive) {
  if (!subscription) {
    return 'Upgrade to access premium features';
  }
  const rawEnd = subscription.currentPeriodEnd;
  const endDate = rawEnd
    ? new Date(rawEnd).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : '';

  const status = subscription.status;
  if (status === 'canceled') {
    return 'Your subscription has ended';
  }
  if (status === 'past_due') {
    return 'Payment failed. Update your payment method in Manage to keep premium access.';
  }
  if (status === 'incomplete' || status === 'unpaid') {
    return 'Payment requires action. Open Manage to finish setup or update your card.';
  }
  if (status === 'incomplete_expired') {
    return 'Payment was not completed in time. Please start a new subscription.';
  }
  if (status === 'paused') {
    return endDate ? `Paused. Access may be limited until ${endDate}.` : 'Subscription is paused.';
  }
  if (subscription.cancelAtPeriodEnd && endDate) {
    if (status === 'trialing' || isTrialActive) {
      return `Trial ends on ${endDate}`;
    }
    return `Subscription cancels on ${endDate}. You keep access until then.`;
  }
  if (endDate) {
    if (status === 'trialing' || isTrialActive) {
      return `Trial renews or converts on ${endDate}`;
    }
    return `Next billing date: ${endDate}`;
  }
  return 'Manage your plan below';
}

const SubscriptionSection = () => {
  const { subscription, loadSubscription, subscriptionLoading, isTrialActive, isFoundingMember } = useAuth();
  const [error, setError] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);

  // Close PricingModal when returning from Stripe checkout
  useEffect(() => {
    const handler = () => {
      setShowPricingModal(false);
    };
    window.addEventListener('subscription:close-pricing-modal', handler);
    return () => window.removeEventListener('subscription:close-pricing-modal', handler);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setReferralLoading(true);
        const data = await getReferralStats();
        setReferralData(data);
      } catch (_) {}
      finally { setReferralLoading(false); }
    };
    load();
  }, []);

  const handleGenerateReferral = async () => {
    try {
      setReferralLoading(true);
      const data = await generateReferralCode();
      setReferralData(data);
    } catch (_) {}
    finally { setReferralLoading(false); }
  };

  const handleCopyReferral = async () => {
    try {
      if (referralData?.shareUrl) {
        await navigator.clipboard.writeText(referralData.shareUrl);
      }
    } catch (_) {}
  };

  const handleShareReferral = async () => {
    if (!referralData?.shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join me on Budgit!', url: referralData.shareUrl });
      } catch (_) {}
    } else {
      handleCopyReferral();
    }
  };

  const handleManageSubscription = async () => {
    try {
      setProcessing(true);
      setError(null);
      const { portalUrl } = await subscriptionService.createPortalSession();
      if (portalUrl) {
        window.location.href = portalUrl;
      } else {
        setError('Unable to open billing portal. Please try again.');
      }
    } catch (err) {
      setError('Failed to open billing portal. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpgrade = async (priceId) => {
    try {
      setProcessing(true);
      const { sessionUrl } = await subscriptionService.createCheckoutSession(priceId);
      window.location.href = sessionUrl;
    } catch (err) {
      setError('Failed to start checkout process');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!subscription?.id) {
      console.log('SubscriptionSection: No subscription ID found:', subscription);
      setError('No active subscription to cancel');
      setCancelDialogOpen(false);
      return;
    }

    try {
      setProcessing(true);
      setError(null); // Clear any previous errors
      console.log('SubscriptionSection: Starting cancellation for ID:', subscription.id);
      const result = await subscriptionService.cancelSubscription();
      console.log('SubscriptionSection: Cancellation API response:', result);
      if (result.success) {
        // Only close the modal after successful cancellation
        setCancelDialogOpen(false);
        // Refresh subscription data
        await loadSubscription(true);
      } else {
        throw new Error(result.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('SubscriptionSection: Cancellation request failed:', err);
      setError(err.message || 'Failed to cancel subscription. Please try again.');
      // Keep the modal open on error
    } finally {
      setProcessing(false);
    }
  };

  if (subscriptionLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Subscription Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{
          p: 3,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: 'row',
            gap: 2
          }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 1,
                  rowGap: 0.75,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" component="span">
                  {subscription ? (isFoundingMember ? 'Founder' : 'Premium') : 'Free'}
                </Typography>
                {isTrialActive && (
                  <Chip label="Trial" size="small" color="primary" sx={{ fontWeight: 600 }} />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {subscription?.provider === 'apple'
                  ? 'This subscription is billed through Apple. On the web you can view status here; cancel or change plans in iOS Settings > Subscriptions.'
                  : getStripeSubscriptionSecondaryText(subscription, isTrialActive)}
              </Typography>
            </Box>
            {subscription ? (
              <Box sx={{
                display: 'flex',
                gap: 1,
                flexDirection: 'column',
                minWidth: '140px'
              }}>
                {subscription?.provider !== 'apple' && subscription?.status ? (
                  <Button
                    variant="outlined"
                    onClick={handleManageSubscription}
                    disabled={processing}
                    size="small"
                    fullWidth
                  >
                    Manage
                  </Button>
                ) : null}
                {subscription?.provider === 'apple' ? (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    Apple billing
                  </Typography>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => setCancelDialogOpen(true)}
                    disabled={processing || subscription?.cancelAtPeriodEnd === true}
                    size="small"
                    fullWidth
                    sx={{
                      ...(subscription?.cancelAtPeriodEnd === true && {
                        color: 'text.secondary',
                        borderColor: 'text.secondary'
                      })
                    }}
                  >
                    {subscription?.cancelAtPeriodEnd === true ? 'Cancels at period end' : 'Cancel'}
                  </Button>
                )}
              </Box>
            ) : (
              <Box sx={{ minWidth: '140px' }}>
                <Button
                  variant="contained"
                  onClick={() => setShowPricingModal(true)}
                  disabled={processing}
                  size="small"
                  fullWidth
                >
                  View Plans
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {/* Invite & Save (outside the gray plan box) */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Invite & Save</Typography>
        </Box>
        {referralData?.referralCode ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" startIcon={<ShareIcon />} onClick={handleShareReferral} disabled={referralLoading}>Share Link</Button>
            <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyReferral} disabled={referralLoading}>Copy Link</Button>
          </Box>
        ) : (
          <Button size="small" variant="outlined" onClick={handleGenerateReferral} disabled={referralLoading}>
            {referralLoading ? 'Generating...' : 'Generate Referral Link'}
          </Button>
        )}
      </Box>

      {/* Cancel Subscription Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            width: '100%',
            maxWidth: '400px',
            p: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          pt: 0,
          px: 0,
          typography: 'h6',
          fontWeight: 'medium'
        }}>
          Cancel Subscription?
        </DialogTitle>
        <DialogContent sx={{ p: 0, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to cancel your subscription? You'll lose access to premium features at the end of your current billing period.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          p: 0,
          gap: 1,
          '& > button': { flex: 1 }
        }}>
          <Button 
            onClick={handleCancel} 
            variant="outlined"
            disabled={processing}
            fullWidth
          >
            Confirm
          </Button>
          <Button 
            onClick={() => setCancelDialogOpen(false)} 
            variant="contained"
            disabled={processing}
            fullWidth
          >
            Keep
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pricing Modal */}
      <PricingModal
        open={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        onUpgrade={handleUpgrade}
      />
    </Box>
  );
};

export default SubscriptionSection; 